import type { Member, MemberToken } from '@firsttable/types';
import { isBrowser } from '@firsttable/functions';
import { normaliseContactPermission } from './analytics/contactPermission';

interface windowWithFBQ extends Window {
  fbq?: any;
}

const func = () => undefined;
const fbq = isBrowser ? (window as windowWithFBQ).fbq || func : {};
const dataLayer = isBrowser ? window.dataLayer || [] : [];

const ecomLayer = (data) => {
  dataLayer.push(data);
};

type SsoAuthToken = Partial<MemberToken> | Partial<Member>;

const resolveAuthMember = (authToken?: SsoAuthToken | null): Partial<Member> | null => {
  if (!authToken) {
    return null;
  }

  if ('member' in authToken) {
    return authToken.member ?? null;
  }

  return 'id' in authToken && authToken.id ? authToken : null;
};

type SsoEventOptions = {
  values?: any;
  attributionPromoCode?: string | null;
};

function ssoEvent(
  type: string,
  authToken: SsoAuthToken,
  logger = null,
  track?: (eventName: string, data?: any) => void,
  identify?: (userId: string | number, data?: any) => void,
  options?: SsoEventOptions
) {
  const member = resolveAuthMember(authToken);
  const values = options?.values;
  // An omitted attributionPromoCode key falls back to the signup value; null means no URL code.
  const promoCode =
    options && 'attributionPromoCode' in options ? options.attributionPromoCode : values?.promoCode;
  const contactPermission =
    normaliseContactPermission(values?.contactPermission) ??
    normaliseContactPermission(member?.contactPermission);
  const action = 'action' in authToken ? authToken.action : undefined;
  const accountStatus = 'accountStatus' in authToken ? authToken.accountStatus : undefined;

  const identifyEvent = () => {
    if (!member?.id) {
      return;
    }

    identify(member.id, {
      id: member.id,
      first_name: member.firstName,
      last_name: member.surname,
      email: member.email,
      avatar: member.avatarURL,
      contact_permission: contactPermission,
      terms_and_privacy: member.termsAndPrivacy,
      current_loyalty_tier: member.currentLoyaltyTier?.definition?.title ?? undefined,
      city_id: member.city?.id,
      city_name: member.city?.menuTitle,
    });
  };
  const trackIdentifyEvent = () => {
    if (!member?.id) {
      return;
    }

    identifyEvent();

    track('account_created', {
      city_id: member.city?.id,
      city_name: member.city?.menuTitle,
      form_email: member.email,
      promo_code: promoCode,
      promo_code_used: Boolean(promoCode),
      contact_permission: contactPermission,
      account_creation_method: type,
      value: 0,
      currency: null,
    });
  };

  if (action === 'RETRIEVED') {
    if (logger) logger.log(`Login: Authenticated ${type} via the FT backend`, authToken);
    identifyEvent();
    if (member?.id) {
      track('account_login', {
        user_id: member.id,
        login_method: type,
      });
    }
  } else if (accountStatus === 'INCOMPLETE') {
    // ignore failed
    if (member?.contactPermission && !member?.termsAndPrivacy) {
      // Sub only
    } else {
      // lead - Some details, no contact permission  -i.e. facebook first step
    }
    /** social sign up */
    if (action === 'CREATED') {
      trackIdentifyEvent();
    }
  } else if (action === 'CREATED') {
    if (logger) logger.log(`Sign up: Authenticated ${type} via the FT backend`, authToken);
    /** email sign up */
    trackIdentifyEvent();
  } else {
    if (logger) logger.log(`Login: Authenticated ${type} via the FT backend`, authToken);
    /** email login */
    identifyEvent();
    if (member?.id) {
      track('account_login', {
        user_id: member.id,
        login_method: type,
      });
    }
  }
}

export { fbq, dataLayer, ecomLayer, resolveAuthMember, ssoEvent };
